import type { Creation, Kind } from "../src/domain/types";
import type { ActivityEntry } from "../src/http/activity-entry";
import type { LibraryEntry } from "../src/domain/library-entry";
import type { Grant, Rank } from "../src/domain/ranks";
import type { RankRecord } from "../src/http/rank-record";
import type { Idea, SelectedThing } from "../src/providers/types";

export interface Who {
  username: string;
  parent: boolean;
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

async function call<T>(path: string, body?: unknown, method?: string): Promise<T> {
  const res = await fetch(path, {
    method: method ?? (body === undefined ? "GET" : "POST"),
    headers: body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: "same-origin",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError((data as { error?: string }).error ?? "Something went wrong.", res.status);
  return data as T;
}

export const api = {
  session: () => call<Who & { username: string | null }>("/api/session"),
  login: (username: string, password: string) => call<Who>("/api/login", { username, password }),
  setPassword: (token: string, password: string) => call<Who>("/api/set-password", { token, password }),
  activity: () => call<{ entries: ActivityEntry[] }>("/api/activity"),
  library: () => call<{ entries: LibraryEntry[] }>("/api/library"),
  saveLibrary: (entries: LibraryEntry[]) => call<{ ok: true }>("/api/library", { entries }, "PUT"),
  allLibraries: () => call<{ libraries: Record<string, LibraryEntry[]> }>("/api/library/all"),
  ranks: () => call<{ builtIn: Rank[]; custom: RankRecord[]; grants: Grant[] }>("/api/ranks"),
  grantRank: (grant: Grant) => call<{ grant: Grant }>("/api/ranks/grant", grant),
  ungrantRank: (grant: Grant) => call<{ ok: true }>("/api/ranks/grant/remove", grant),
  designRank: (prompt: string) => call<{ rank: Rank }>("/api/ranks/design", { prompt }),
  submitRank: (rank: Rank) => call<{ record: RankRecord }>("/api/ranks", { rank }),
  reviewRank: (id: string, decision: "approve" | "reject") => call<{ record: RankRecord }>(`/api/ranks/${encodeURIComponent(id)}/review`, { decision }),
  logout: () => call<{ ok: true }>("/api/logout", {}),
  create: (prompt: string, kind: Kind) => call<{ creation: Creation }>("/api/create", { prompt, kind }),
  texture: (prompt: string, kind: Kind) => call<{ png: string }>("/api/texture", { prompt, kind }),
  ideas: (kind: Kind, selected: SelectedThing | null, hint: string) =>
    call<{ ideas: Idea[] }>("/api/ideas", { kind, selected, hint }),
};
